import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingCart, Search, Filter, Plus, Minus, Trash2, X, ChevronLeft, Package, Gauge, Wrench, Cog, Check, ChevronRight, ArrowLeft, Info, User, UserPlus, LogOut, History, MapPin, Mail, Phone, Lock, Eye, EyeOff } from 'lucide-react';
import { useSiteMeta } from './hooks/useSiteMeta';
import { VacationBanner } from './components/VacationBanner';
import { StaticBanner } from './components/StaticBanner';
import { AnimationEmbed } from './components/AnimationEmbed';
import { useFlexfuelOffer } from './hooks/useFlexfuelOffer';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_DARK = "https://customer-assets.emergentagent.com/job_67cb5430-fb0d-4c25-a9f8-a4cee239dafe/artifacts/1vm4vdax_IMG_1412.png";

// French VAT rate (20%)
const TVA_RATE = 0.20;

// Helper function to calculate price with TVA
const calculateTTC = (priceHT) => priceHT * (1 + TVA_RATE);

// Category icons mapping
const categoryIcons = {
  turbos: Gauge,
  kits: Package,
  accessoires: Wrench,
  pieces: Cog
};

const Shop = () => {
  // CMS-driven SEO title/description/H1 — falls back to hardcoded values so admins
  // can rename the shop from the Content tab without touching code.
  const [shopSeo, setShopSeo] = useState({});
  useEffect(() => {
    axios.get(`${API}/api/content/shop_landing`)
      .then((r) => setShopSeo((r.data && r.data.seo) || {}))
      .catch(() => {});
  }, []);
  useSiteMeta({
    title: shopSeo.title || "Boutique en Ligne — Auvergne Tech Racing Clermont",
    description: shopSeo.description || "Commandez vos prestations en ligne : conversion Flexfuel E85, reprogrammation Stage 1 & 2, service fichiers ECU. Garage à Clermont-Ferrand (63).",
    canonical_path: "/shop",
  });

  const flexfuelOffer = useFlexfuelOffer();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showCheckoutChoice, setShowCheckoutChoice] = useState(false); // New: show login/register/guest choice
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'product'
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  
  // Customer Account State
  const [customer, setCustomer] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'register' | 'forgot' | 'reset'
  const [authFromCheckout, setAuthFromCheckout] = useState(false); // Track if auth was triggered from checkout
  const [showAccount, setShowAccount] = useState(false);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [resetToken, setResetToken] = useState(''); // Token extracted from URL for password reset
  const [authForm, setAuthForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    city: '',
    postal_code: ''
  });
  const [authLoading, setAuthLoading] = useState(false);

  // Change password (when logged in) form
  const [changePwdForm, setChangePwdForm] = useState({
    current_password: '',
    new_password: '',
    confirm_new_password: '',
  });
  const [changePwdLoading, setChangePwdLoading] = useState(false);
  
  // Checkout form
  const [checkoutForm, setCheckoutForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    customer_city: '',
    customer_postal_code: ''
  });

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('atr_shop_cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
    
    // Check for saved customer session
    const savedToken = localStorage.getItem('atr_shop_token');
    if (savedToken) {
      fetchCustomerProfile(savedToken);
    }

    // Detect password-reset token in URL
    const params = new URLSearchParams(window.location.search);
    const rt = params.get('reset_token');
    if (rt) {
      setResetToken(rt);
      setAuthMode('reset');
      setShowAuthModal(true);
    }
  }, []);

  // Save cart to localStorage
  useEffect(() => {
    localStorage.setItem('atr_shop_cart', JSON.stringify(cart));
  }, [cart]);
  
  // Fetch customer profile
  const fetchCustomerProfile = async (token) => {
    try {
      const response = await axios.get(`${API}/api/shop/auth/me?token=${token}`);
      setCustomer(response.data);
      // Pre-fill checkout form with customer data
      setCheckoutForm({
        customer_name: `${response.data.first_name} ${response.data.last_name}`.trim(),
        customer_email: response.data.email,
        customer_phone: response.data.phone || '',
        customer_address: response.data.address || '',
        customer_city: response.data.city || '',
        customer_postal_code: response.data.postal_code || ''
      });
    } catch (error) {
      // Token expired or invalid
      localStorage.removeItem('atr_shop_token');
      setCustomer(null);
    }
  };
  
  // Fetch customer orders
  const fetchCustomerOrders = async () => {
    const token = localStorage.getItem('atr_shop_token');
    if (!token) return;
    try {
      const response = await axios.get(`${API}/api/shop/customer/orders?token=${token}`);
      setCustomerOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };
  
  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const response = await axios.post(`${API}/api/shop/auth/login`, {
        email: authForm.email,
        password: authForm.password
      });
      localStorage.setItem('atr_shop_token', response.data.token);
      setCustomer(response.data.customer);
      setShowAuthModal(false);
      toast.success('Connexion réussie !');
      // Pre-fill checkout form
      setCheckoutForm({
        customer_name: `${response.data.customer.first_name} ${response.data.customer.last_name}`.trim(),
        customer_email: response.data.customer.email,
        customer_phone: response.data.customer.phone || '',
        customer_address: response.data.customer.address || '',
        customer_city: response.data.customer.city || '',
        customer_postal_code: response.data.customer.postal_code || ''
      });
      setAuthForm({ email: '', password: '', confirmPassword: '', first_name: '', last_name: '', phone: '', address: '', city: '', postal_code: '' });
      
      // If came from checkout, continue to checkout
      if (authFromCheckout) {
        setAuthFromCheckout(false);
        setShowCheckout(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setAuthLoading(false);
    }
  };
  
  // Handle register
  const handleRegister = async (e) => {
    e.preventDefault();
    if (authForm.password !== authForm.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (authForm.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setAuthLoading(true);
    try {
      await axios.post(`${API}/api/shop/auth/register`, {
        email: authForm.email,
        password: authForm.password,
        first_name: authForm.first_name,
        last_name: authForm.last_name,
        phone: authForm.phone,
        address: authForm.address,
        city: authForm.city,
        postal_code: authForm.postal_code
      });
      toast.success('Compte créé ! Vous pouvez maintenant vous connecter.');
      setAuthMode('login');
      setAuthForm({ ...authForm, password: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setAuthLoading(false);
    }
  };
  
  // Handle forgot password — request reset email
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!authForm.email) {
      toast.error('Veuillez saisir votre email');
      return;
    }
    setAuthLoading(true);
    try {
      const formData = new FormData();
      formData.append('email', authForm.email);
      await axios.post(`${API}/api/shop/auth/forgot-password`, formData);
      toast.success('Si un compte existe, un email de réinitialisation vient d\'être envoyé.');
      setAuthMode('login');
      setAuthForm({ ...authForm, password: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi de l\'email');
    } finally {
      setAuthLoading(false);
    }
  };

  // Handle reset password — submit new password with token from URL
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (authForm.password !== authForm.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (authForm.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setAuthLoading(true);
    try {
      const formData = new FormData();
      formData.append('token', resetToken);
      formData.append('new_password', authForm.password);
      await axios.post(`${API}/api/shop/auth/reset-password`, formData);
      toast.success('Mot de passe réinitialisé ! Vous pouvez maintenant vous connecter.');
      setAuthMode('login');
      setResetToken('');
      setAuthForm({ email: '', password: '', confirmPassword: '', first_name: '', last_name: '', phone: '', address: '', city: '', postal_code: '' });
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setAuthLoading(false);
    }
  };

  // Handle change password (for logged-in customer)
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (changePwdForm.new_password !== changePwdForm.confirm_new_password) {
      toast.error('Les nouveaux mots de passe ne correspondent pas');
      return;
    }
    if (changePwdForm.new_password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    const token = localStorage.getItem('atr_shop_token');
    if (!token) {
      toast.error('Session expirée, veuillez vous reconnecter');
      return;
    }
    setChangePwdLoading(true);
    try {
      const fd = new FormData();
      fd.append('current_password', changePwdForm.current_password);
      fd.append('new_password', changePwdForm.new_password);
      await axios.put(`${API}/api/shop/auth/password?token=${token}`, fd);
      toast.success('Mot de passe modifié avec succès');
      setChangePwdForm({ current_password: '', new_password: '', confirm_new_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setChangePwdLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    const token = localStorage.getItem('atr_shop_token');
    if (token) {
      try {
        await axios.post(`${API}/api/shop/auth/logout?token=${token}`);
      } catch (error) {
        // Ignore logout errors
      }
    }
    localStorage.removeItem('atr_shop_token');
    setCustomer(null);
    setShowAccount(false);
    setCheckoutForm({
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      customer_address: '',
      customer_city: '',
      customer_postal_code: ''
    });
    toast.success('Déconnexion réussie');
  };
  
  // Update customer profile
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('atr_shop_token');
    if (!token) return;
    
    setAuthLoading(true);
    try {
      const formData = new FormData();
      formData.append('first_name', customer.first_name || '');
      formData.append('last_name', customer.last_name || '');
      formData.append('phone', customer.phone || '');
      formData.append('address', customer.address || '');
      formData.append('city', customer.city || '');
      formData.append('postal_code', customer.postal_code || '');
      
      await axios.put(`${API}/api/shop/auth/profile?token=${token}`, formData);
      toast.success('Profil mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setAuthLoading(false);
    }
  };

  // Check for payment status on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    
    if (payment === 'success' && sessionId) {
      checkPaymentStatus(sessionId);
      // Clear cart on successful payment
      setCart([]);
      localStorage.removeItem('atr_shop_cart');
      // Clean URL
      window.history.replaceState({}, document.title, '/shop');
    } else if (payment === 'cancelled') {
      toast.error('Paiement annulé');
      // Clean URL
      window.history.replaceState({}, document.title, '/shop');
    }
  }, []);

  // Fetch categories and products
  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, [selectedCategory, searchQuery]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/api/shop/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = `${API}/api/shop/products`;
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/api/shop/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        setCart([]); // Clear cart after successful payment
        localStorage.removeItem('atr_shop_cart');
      } else if (response.data.status === 'expired') {
        setPaymentStatus('expired');
      } else {
        // Continue polling
        setTimeout(() => checkPaymentStatus(sessionId), 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const addToCart = (product, quantity = 1) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product_id === product.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prevCart, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        shipping_cost: product.shipping_cost || 0,
        image: product.images?.[0] || '',
        quantity
      }];
    });
    toast.success(`${product.name} ajouté au panier`);
  };

  const updateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item.product_id === productId ? { ...item, quantity } : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.product_id !== productId));
  };

  // Cart totals with TVA and Shipping
  const cartTotalHT = cart.reduce((total, item) => total + item.price * item.quantity, 0);
  const cartShippingHT = cart.reduce((total, item) => total + (item.shipping_cost || 0) * item.quantity, 0);
  const cartSubtotalHT = cartTotalHT + cartShippingHT;
  const cartTotalTTC = calculateTTC(cartSubtotalHT);
  const cartTVA = cartTotalTTC - cartSubtotalHT;
  const cartItemsCount = cart.reduce((count, item) => count + item.quantity, 0);

  const handleCheckout = async () => {
    if (!checkoutForm.customer_name || !checkoutForm.customer_email) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setCheckoutLoading(true);
    try {
      const response = await axios.post(`${API}/api/shop/checkout`, {
        items: cart.map(item => ({ product_id: item.product_id, quantity: item.quantity })),
        ...checkoutForm,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Erreur lors du paiement');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Payment success screen
  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="bg-[#121212] border border-white/10 rounded-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Paiement réussi !</h2>
          <p className="text-gray-400 mb-6">
            Merci pour votre commande. Vous recevrez un email de confirmation sous peu.
          </p>
          <button
            onClick={() => {
              setPaymentStatus(null);
              window.history.replaceState({}, '', '/shop');
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-bold transition-colors"
          >
            Continuer les achats
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Visually-hidden H1 for SEO — the visible design intentionally has no big heading */}
      <h1 className="sr-only">
        {shopSeo.h1_prefix
          ? `${shopSeo.h1_prefix}${shopSeo.h1_highlight ? " " + shopSeo.h1_highlight : ""}`
          : "Boutique & Prestations — Flexfuel E85 et Reprogrammation à Clermont-Ferrand"}
      </h1>
      {/* Header */}
      <StaticBanner inline />
      <VacationBanner />
      <header className="bg-[#121212] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <a href="/" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
                <ChevronLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Retour au site</span>
              </a>
              <a href="/" className="flex items-center">
                <img 
                  src={LOGO_DARK} 
                  alt="ATR Shop" 
                  className="h-8 w-auto"
                />
              </a>
            </div>
            
            {/* Search */}
            <div className="flex-1 max-w-md mx-4 hidden md:block">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  placeholder="Rechercher un produit..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
                />
              </div>
            </div>
            
            {/* Account & Cart buttons */}
            <div className="flex items-center gap-2">
              {customer ? (
                <button
                  onClick={() => { setShowAccount(true); fetchCustomerOrders(); }}
                  className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <User className="w-5 h-5" />
                  <span className="hidden sm:inline">{customer.first_name || 'Mon compte'}</span>
                </button>
              ) : (
                <button
                  onClick={() => { setShowAuthModal(true); setAuthMode('login'); }}
                  className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <User className="w-5 h-5" />
                  <span className="hidden sm:inline">Connexion</span>
                </button>
              )}
              
              <button
                onClick={() => setShowCart(true)}
                className="relative bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <ShoppingCart className="w-5 h-5" />
                <span className="hidden sm:inline">Panier</span>
                {cartItemsCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-white text-red-600 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {cartItemsCount}
                  </span>
                )}
              </button>
            </div>
          </div>
          
          {/* Mobile search */}
          <div className="mt-4 md:hidden">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-red-500"
              />
            </div>
          </div>
        </div>
      </header>

      <section className="pt-10 pb-2 px-4" data-testid="shop-animation-section">
        <AnimationEmbed src="/boutique.html" title="La boutique ATR" testId="boutique-animation" />
      </section>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Categories */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-[#121212] border border-white/10 rounded-lg p-4 sticky top-24">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Filter className="w-5 h-5 text-red-500" />
                Catégories
              </h3>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                    !selectedCategory ? 'bg-red-600 text-white' : 'text-gray-400 hover:bg-white/5'
                  }`}
                >
                  Tous les produits
                </button>
                {categories.map((cat) => {
                  const IconComponent = categoryIcons[cat.id] || Package;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                        selectedCategory === cat.id ? 'bg-red-600 text-white' : 'text-gray-400 hover:bg-white/5'
                      }`}
                    >
                      <IconComponent className="w-4 h-4" />
                      {cat.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* Main content - Products */}
          <main className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Aucun produit trouvé</h3>
                <p className="text-gray-400">
                  {searchQuery ? 'Essayez une autre recherche' : 'Les produits arrivent bientôt !'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden hover:border-red-500/50 transition-colors group"
                  >
                    {/* Product image */}
                    <div 
                      className="aspect-square bg-white/5 relative cursor-pointer"
                      onClick={() => {
                        setSelectedProduct(product);
                        setSelectedImageIndex(0);
                        setViewMode('product');
                      }}
                    >
                      {product.images?.[0] ? (
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-16 h-16 text-gray-600" />
                        </div>
                      )}
                      {product.original_price > product.price && (
                        <span className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded">
                          Promo
                        </span>
                      )}
                      {product.featured && (
                        <span className="absolute top-2 right-2 bg-yellow-500 text-black text-xs px-2 py-1 rounded font-bold">
                          Populaire
                        </span>
                      )}
                    </div>
                    
                    {/* Product info */}
                    <div className="p-4">
                      {product.brand && (
                        <p className="text-red-500 text-sm font-medium mb-1">{product.brand}</p>
                      )}
                      <h3 
                        className="text-white font-bold mb-2 cursor-pointer hover:text-red-500 transition-colors"
                        onClick={() => {
                          setSelectedProduct(product);
                          setSelectedImageIndex(0);
                          setViewMode('product');
                        }}
                      >
                        {product.name}
                      </h3>
                      <p className="text-gray-400 text-sm mb-3 line-clamp-2">
                        {product.short_description || product.description}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-xl font-bold text-white">{calculateTTC(product.price).toFixed(2)} €</span>
                          <span className="text-gray-500 text-xs ml-1">TTC</span>
                          {product.original_price > product.price && (
                            <span className="text-gray-500 line-through text-sm ml-2">
                              {calculateTTC(product.original_price).toFixed(2)} €
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => addToCart(product)}
                          className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition-colors"
                          title="Ajouter au panier"
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                      </div>
                      
                      {product.show_stock !== false && product.stock <= 5 && product.stock > 0 && (
                        <p className="text-orange-500 text-sm mt-2">Plus que {product.stock} en stock</p>
                      )}
                      {product.show_stock !== false && product.stock === 0 && (
                        <p className="text-red-500 text-sm mt-2">Rupture de stock</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ============== SEO CONTENT SECTION ============== */}
            <section
              className="mt-20 mb-8 border-t border-white/10 pt-12"
              data-testid="shop-seo-content"
              aria-labelledby="shop-seo-heading"
            >
              <div className="max-w-4xl mx-auto space-y-8 text-gray-300 leading-relaxed">
                <div>
                  <h2 id="shop-seo-heading" className="text-2xl md:text-3xl font-bold text-white mb-4">
                    La boutique auto Auvergne Tech Racing à Clermont-Ferrand
                  </h2>
                  <p>
                    Notre boutique en ligne met à votre disposition l&apos;ensemble des prestations phares
                    de notre garage Auvergne Tech Racing, spécialiste des <strong>véhicules essence, diesel et hybrides</strong> à
                    Clermont-Ferrand (63). Achetez et réservez en quelques clics votre <strong>conversion Flexfuel E85</strong>,
                    votre <strong>reprogrammation moteur Stage 1</strong> ou <strong>Stage 2</strong>, ainsi que nos forfaits d&apos;entretien
                    mécanique, avec un règlement 100 % sécurisé par Stripe. Chaque commande est confirmée par email et
                    prise en charge par notre atelier situé au 47 rue Jules Verne, 63100 Clermont-Ferrand.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Pourquoi commander vos prestations en ligne ?</h3>
                  <p>
                    Réserver depuis notre boutique vous garantit un <strong>tarif transparent</strong> affiché en TTC, un
                    <strong> créneau atelier prioritaire</strong> et l&apos;accompagnement d&apos;un technicien passionné à
                    chaque étape. Vous évitez les allers-retours téléphoniques pour un devis : le prix affiché est le
                    prix final, sans surprise. Nos prestations sont éligibles à divers financements
                    et nos conversions Flexfuel bénéficient de <strong>l&apos;homologation par arrêté</strong> pour circuler
                    en toute légalité sur le territoire français.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Nos catégories phares</h3>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>
                      <strong>Conversion Flexfuel E85</strong> — kit boîtier certifié installé en atelier, éligible toutes marques
                      (Peugeot, Renault, Citroën, Ford, Audi, BMW, Volkswagen, Mercedes…), à partir de {flexfuelOffer.label}.
                    </li>
                    <li>
                      <strong>Reprogrammation moteur Stage 1 & Stage 2</strong> — gain de puissance et de couple mesurable sur banc,
                      cartographie sur mesure, garantie moteur préservée par notre expertise.
                    </li>
                    <li>
                      <strong>Service Fichiers ECU en ligne</strong> — envoyez votre fichier moteur et recevez la cartographie
                      optimisée sous 24 h ouvrées, avec support dédié pour concessionnaires et préparateurs.
                    </li>
                    <li>
                      <strong>Entretien mécanique</strong> — vidange, embrayage, distribution, freinage,
                      diagnostic électronique multimarque, prêts de véhicule sur demande.
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">Un service local, un savoir-faire national</h3>
                  <p>
                    Basés à Clermont-Ferrand au cœur de l&apos;Auvergne, nous accueillons chaque semaine des clients
                    venus de <strong>toute la région Auvergne-Rhône-Alpes</strong> — Riom, Vichy, Issoire, Aurillac, Le Puy-en-Velay,
                    Moulins, Aubière — mais aussi de plus loin pour nos prestations de reprogrammation et Flexfuel.
                    Notre atelier dispose d&apos;équipements <strong>Bosch, Alientech et Magic Motorsport</strong>, gage
                    de la précision et de la fiabilité de nos interventions sur véhicules essence, diesel et hybrides.
                  </p>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      {/* Full Product Page */}
      {viewMode === 'product' && selectedProduct && (
        <div className="fixed inset-0 bg-[#0a0a0a] z-50 overflow-y-auto">
          {/* Product Page Header */}
          <header className="bg-[#121212] border-b border-white/10 sticky top-0 z-40">
            <div className="max-w-7xl mx-auto px-4 py-4">
              <div className="flex items-center justify-between">
                <button 
                  onClick={() => {
                    setViewMode('list');
                    setSelectedProduct(null);
                  }}
                  className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span>Retour à la boutique</span>
                </button>
                
                <button
                  onClick={() => setShowCart(true)}
                  className="relative bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  <ShoppingCart className="w-5 h-5" />
                  <span className="hidden sm:inline">Panier</span>
                  {cartItemsCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-white text-red-600 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                      {cartItemsCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </header>

          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Product Images Gallery */}
              <div className="space-y-4">
                {/* Main Image */}
                <div className="aspect-square bg-[#121212] border border-white/10 rounded-lg overflow-hidden">
                  {selectedProduct.images?.[selectedImageIndex] ? (
                    <img
                      src={selectedProduct.images[selectedImageIndex]}
                      alt={selectedProduct.name}
                      className="w-full h-full object-contain bg-white"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-white/5">
                      <Package className="w-32 h-32 text-gray-600" />
                    </div>
                  )}
                </div>
                
                {/* Thumbnails */}
                {selectedProduct.images && selectedProduct.images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {selectedProduct.images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImageIndex(idx)}
                        className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                          idx === selectedImageIndex ? 'border-red-500' : 'border-white/10 hover:border-white/30'
                        }`}
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Product Details */}
              <div className="space-y-6">
                {/* Brand & Title */}
                <div>
                  {selectedProduct.brand && (
                    <p className="text-red-500 font-medium text-lg mb-2">{selectedProduct.brand}</p>
                  )}
                  <h1 className="text-3xl font-bold text-white mb-2">{selectedProduct.name}</h1>
                  {selectedProduct.reference && (
                    <p className="text-gray-500">Réf: {selectedProduct.reference}</p>
                  )}
                </div>

                {/* Price */}
                <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="text-4xl font-bold text-white">{calculateTTC(selectedProduct.price).toFixed(2)} €</span>
                    <span className="text-gray-400">TTC</span>
                  </div>
                  <p className="text-sm text-gray-500 italic">
                    Prix TTC, TVA (20%) incluse. Frais de livraison calculés lors de la commande.
                  </p>
                  {selectedProduct.original_price > selectedProduct.price && (
                    <div className="mt-2">
                      <span className="text-gray-500 line-through text-lg">
                        {calculateTTC(selectedProduct.original_price).toFixed(2)} € TTC
                      </span>
                      <span className="ml-2 bg-red-600 text-white text-sm px-2 py-1 rounded">
                        -{Math.round((1 - selectedProduct.price / selectedProduct.original_price) * 100)}%
                      </span>
                    </div>
                  )}
                </div>

                {/* Stock Status - only show if show_stock is true */}
                {selectedProduct.show_stock !== false && (
                  <div className="flex items-center gap-2">
                    {selectedProduct.stock > 5 ? (
                      <>
                        <Check className="w-5 h-5 text-green-500" />
                        <span className="text-green-500 font-medium">En stock</span>
                      </>
                    ) : selectedProduct.stock > 0 ? (
                      <>
                        <Info className="w-5 h-5 text-orange-500" />
                        <span className="text-orange-500 font-medium">Plus que {selectedProduct.stock} en stock</span>
                      </>
                    ) : (
                      <>
                        <X className="w-5 h-5 text-red-500" />
                        <span className="text-red-500 font-medium">Rupture de stock</span>
                      </>
                    )}
                  </div>
                )}

                {/* Add to Cart */}
                <button
                  onClick={() => {
                    addToCart(selectedProduct);
                  }}
                  disabled={selectedProduct.stock === 0}
                  className={`w-full py-4 rounded-lg font-bold text-lg flex items-center justify-center gap-3 transition-colors ${
                    selectedProduct.stock === 0
                      ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                  }`}
                >
                  <ShoppingCart className="w-6 h-6" />
                  {selectedProduct.stock === 0 ? 'Rupture de stock' : 'Ajouter au panier'}
                </button>
              </div>
            </div>

            {/* Full Description */}
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <Info className="w-6 h-6 text-red-500" />
                Description du produit
              </h2>
              <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
                <p className="text-gray-300 whitespace-pre-line leading-relaxed text-lg">
                  {selectedProduct.description || 'Aucune description disponible.'}
                </p>
              </div>
            </div>

            {/* Detailed Specifications - Full Width */}
            {selectedProduct.specifications && Object.keys(selectedProduct.specifications).length > 0 && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <Cog className="w-6 h-6 text-red-500" />
                  Caractéristiques techniques & Compatibilité
                </h2>
                <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
                  {Object.entries(selectedProduct.specifications).map(([key, value]) => (
                    <div key={key} className="mb-6 last:mb-0">
                      {/* Parse and format the specifications text */}
                      <div className="text-gray-300 leading-relaxed">
                        {value.split('\n').map((line, idx) => {
                          // Check if line is a table row (contains |)
                          if (line.includes(' | ')) {
                            const cells = line.split(' | ');
                            const isHeader = idx === 0 || line.toLowerCase().includes('modèle') || line.toLowerCase().includes('model');
                            return (
                              <div 
                                key={idx} 
                                className={`grid gap-2 py-2 px-3 ${isHeader ? 'bg-red-600/20 font-bold text-white' : 'bg-white/5 hover:bg-white/10'} ${idx > 0 ? 'border-t border-white/10' : 'rounded-t-lg'}`}
                                style={{ gridTemplateColumns: `repeat(${Math.min(cells.length, 6)}, minmax(0, 1fr))` }}
                              >
                                {cells.slice(0, 6).map((cell, cellIdx) => (
                                  <span key={cellIdx} className={`text-sm ${isHeader ? 'text-white' : 'text-gray-300'} truncate`} title={cell}>
                                    {cell}
                                  </span>
                                ))}
                              </div>
                            );
                          }
                          // Check if line is a bullet point
                          if (line.startsWith('•')) {
                            return (
                              <div key={idx} className="flex items-start gap-2 py-1">
                                <span className="text-red-500 mt-1">•</span>
                                <span className="text-gray-300">{line.substring(1).trim()}</span>
                              </div>
                            );
                          }
                          // Check if line is a warning/attention
                          if (line.toLowerCase().includes('attention')) {
                            return (
                              <div key={idx} className="bg-orange-500/20 border border-orange-500/30 rounded-lg p-4 my-3">
                                <p className="text-orange-400 font-medium">{line}</p>
                              </div>
                            );
                          }
                          // Check if line contains power info
                          if (line.toLowerCase().includes('power range') || line.toLowerCase().includes('puissance')) {
                            return (
                              <div key={idx} className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 my-3">
                                <p className="text-green-400 font-bold text-lg">{line}</p>
                              </div>
                            );
                          }
                          // Regular text
                          if (line.trim()) {
                            return <p key={idx} className="py-1 text-gray-300">{line}</p>;
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Product Info Cards */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedProduct.brand && (
                <div className="bg-[#121212] border border-white/10 rounded-lg p-4 text-center">
                  <p className="text-gray-400 text-sm mb-1">Marque</p>
                  <p className="text-white font-bold text-lg">{selectedProduct.brand}</p>
                </div>
              )}
              {selectedProduct.reference && (
                <div className="bg-[#121212] border border-white/10 rounded-lg p-4 text-center">
                  <p className="text-gray-400 text-sm mb-1">Référence / SKU</p>
                  <p className="text-white font-bold text-lg">{selectedProduct.reference}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cart sidebar */}
      {showCart && (
        <div className="fixed inset-0 bg-black/80 z-50 flex justify-end">
          <div className="bg-[#121212] w-full max-w-md h-full flex flex-col">
            <div className="p-4 border-b border-white/10 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <ShoppingCart className="w-6 h-6 text-red-500" />
                Panier ({cartItemsCount})
              </h2>
              <button onClick={() => setShowCart(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {cart.length === 0 ? (
                <div className="text-center py-10">
                  <ShoppingCart className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Votre panier est vide</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map((item) => (
                    <div key={item.product_id} className="bg-white/5 rounded-lg p-4 flex gap-4">
                      <div className="w-20 h-20 bg-white/10 rounded-lg overflow-hidden flex-shrink-0">
                        {item.image ? (
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-8 h-8 text-gray-600" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-white font-medium mb-1">{item.name}</h4>
                        <p className="text-red-500 font-bold">{calculateTTC(item.price).toFixed(2)} € TTC</p>
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={() => updateCartQuantity(item.product_id, item.quantity - 1)}
                            className="bg-white/10 hover:bg-white/20 text-white p-1 rounded"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="text-white w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateCartQuantity(item.product_id, item.quantity + 1)}
                            className="bg-white/10 hover:bg-white/20 text-white p-1 rounded"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeFromCart(item.product_id)}
                            className="ml-auto text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {cart.length > 0 && (
              <div className="p-4 border-t border-white/10">
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Sous-total produits HT</span>
                    <span className="text-gray-400">{cartTotalHT.toFixed(2)} €</span>
                  </div>
                  {cartShippingHT > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Frais de port HT</span>
                      <span className="text-gray-400">{cartShippingHT.toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">TVA (20%)</span>
                    <span className="text-gray-400">{cartTVA.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-lg pt-2 border-t border-white/10">
                    <span className="text-white font-bold">Total TTC</span>
                    <span className="text-red-500 font-bold">{cartTotalTTC.toFixed(2)} €</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowCart(false);
                    if (customer) {
                      // Already logged in, go directly to checkout
                      setShowCheckout(true);
                    } else {
                      // Not logged in, show choice modal
                      setShowCheckoutChoice(true);
                    }
                  }}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-bold transition-colors"
                >
                  Commander
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Checkout Choice Modal - Login/Register/Guest */}
      {showCheckoutChoice && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#121212] border border-white/10 rounded-lg max-w-md w-full">
            <div className="p-4 border-b border-white/10 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">Commander</h2>
              <button onClick={() => setShowCheckoutChoice(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <p className="text-gray-400 text-center mb-6">
                Comment souhaitez-vous continuer ?
              </p>
              
              {/* Login option */}
              <button
                onClick={() => {
                  setShowCheckoutChoice(false);
                  setAuthFromCheckout(true);
                  setAuthMode('login');
                  setShowAuthModal(true);
                }}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-lg font-bold transition-colors flex items-center justify-center gap-3"
              >
                <User className="w-5 h-5" />
                Me connecter
              </button>
              
              {/* Register option */}
              <button
                onClick={() => {
                  setShowCheckoutChoice(false);
                  setAuthFromCheckout(true);
                  setAuthMode('register');
                  setShowAuthModal(true);
                }}
                className="w-full bg-white/10 hover:bg-white/20 text-white py-4 rounded-lg font-bold transition-colors flex items-center justify-center gap-3 border border-white/20"
              >
                <UserPlus className="w-5 h-5" />
                Créer un compte
              </button>
              
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-[#121212] text-gray-500">ou</span>
                </div>
              </div>
              
              {/* Guest option */}
              <button
                onClick={() => {
                  setShowCheckoutChoice(false);
                  setShowCheckout(true);
                }}
                className="w-full bg-transparent hover:bg-white/5 text-gray-400 hover:text-white py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                Continuer en tant qu'invité
                <ChevronRight className="w-4 h-4" />
              </button>
              
              <p className="text-xs text-gray-500 text-center mt-4">
                En créant un compte, vous pourrez suivre vos commandes et enregistrer vos adresses.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Checkout modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#121212] border border-white/10 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#121212] border-b border-white/10 p-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">Finaliser la commande</h2>
              <button onClick={() => setShowCheckout(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Order summary */}
              <div className="bg-white/5 rounded-lg p-4 mb-6">
                <h3 className="text-white font-bold mb-3">Récapitulatif</h3>
                {cart.map((item) => (
                  <div key={item.product_id} className="py-2 text-sm border-b border-white/5 last:border-0">
                    <div className="flex justify-between">
                      <span className="text-gray-400">{item.name} x{item.quantity}</span>
                      <span className="text-white">{calculateTTC(item.price * item.quantity).toFixed(2)} €</span>
                    </div>
                    {(item.shipping_cost || 0) > 0 && (
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>↳ Frais de port</span>
                        <span>{calculateTTC(item.shipping_cost * item.quantity).toFixed(2)} €</span>
                      </div>
                    )}
                  </div>
                ))}
                <div className="border-t border-white/10 mt-2 pt-2 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Sous-total produits HT</span>
                    <span className="text-gray-400">{cartTotalHT.toFixed(2)} €</span>
                  </div>
                  {cartShippingHT > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Frais de port HT</span>
                      <span className="text-gray-400">{cartShippingHT.toFixed(2)} €</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">TVA (20%)</span>
                    <span className="text-gray-400">{cartTVA.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between font-bold pt-2 border-t border-white/10">
                    <span className="text-white">Total TTC</span>
                    <span className="text-red-500">{cartTotalTTC.toFixed(2)} €</span>
                  </div>
                </div>
              </div>
              
              {/* Customer form */}
              <form className="space-y-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Nom complet *</label>
                  <input
                    type="text"
                    value={checkoutForm.customer_name}
                    onChange={(e) => setCheckoutForm({...checkoutForm, customer_name: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Email *</label>
                  <input
                    type="email"
                    value={checkoutForm.customer_email}
                    onChange={(e) => setCheckoutForm({...checkoutForm, customer_email: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={checkoutForm.customer_phone}
                    onChange={(e) => setCheckoutForm({...checkoutForm, customer_phone: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-red-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Adresse</label>
                  <input
                    type="text"
                    value={checkoutForm.customer_address}
                    onChange={(e) => setCheckoutForm({...checkoutForm, customer_address: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-red-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Ville</label>
                    <input
                      type="text"
                      value={checkoutForm.customer_city}
                      onChange={(e) => setCheckoutForm({...checkoutForm, customer_city: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-red-500"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Code postal</label>
                    <input
                      type="text"
                      value={checkoutForm.customer_postal_code}
                      onChange={(e) => setCheckoutForm({...checkoutForm, customer_postal_code: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-red-500"
                    />
                  </div>
                </div>
              </form>
              
              <button
                onClick={handleCheckout}
                disabled={checkoutLoading}
                className="w-full mt-6 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white py-3 rounded-lg font-bold transition-colors flex items-center justify-center gap-2"
              >
                {checkoutLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                    Redirection vers le paiement...
                  </>
                ) : (
                  <>
                    Payer {cartTotalTTC.toFixed(2)} € TTC
                  </>
                )}
              </button>
              
              <p className="text-gray-500 text-sm text-center mt-4">
                Paiement sécurisé par Stripe
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a1a1a] rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white" data-testid="shop-auth-modal-title">
                  {authMode === 'login' && 'Connexion'}
                  {authMode === 'register' && 'Créer un compte'}
                  {authMode === 'forgot' && 'Mot de passe oublié'}
                  {authMode === 'reset' && 'Nouveau mot de passe'}
                </h2>
                <button
                  onClick={() => {
                    setShowAuthModal(false);
                    if (authMode === 'reset') {
                      setResetToken('');
                      setAuthMode('login');
                      window.history.replaceState({}, '', window.location.pathname);
                    }
                  }}
                  className="text-gray-400 hover:text-white"
                  data-testid="shop-auth-modal-close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* FORGOT PASSWORD MODE */}
              {authMode === 'forgot' && (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-gray-400 text-sm">
                    Saisissez votre email. Si un compte existe, vous recevrez un lien pour réinitialiser votre mot de passe.
                  </p>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        data-testid="shop-forgot-email"
                        type="email"
                        value={authForm.email}
                        onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white"
                        placeholder="votre@email.com"
                        required
                      />
                    </div>
                  </div>
                  <button
                    data-testid="shop-forgot-submit"
                    type="submit"
                    disabled={authLoading}
                    className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white py-3 rounded-lg font-bold transition-colors"
                  >
                    {authLoading ? 'Envoi...' : 'Envoyer le lien de réinitialisation'}
                  </button>
                  <div className="text-center pt-2">
                    <button
                      data-testid="shop-forgot-back"
                      type="button"
                      onClick={() => setAuthMode('login')}
                      className="text-gray-400 hover:text-white text-sm"
                    >
                      ← Retour à la connexion
                    </button>
                  </div>
                </form>
              )}

              {/* RESET PASSWORD MODE (from email link) */}
              {authMode === 'reset' && (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <p className="text-gray-400 text-sm">
                    Choisissez un nouveau mot de passe pour votre compte.
                  </p>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Nouveau mot de passe</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        data-testid="shop-reset-password"
                        type="password"
                        value={authForm.password}
                        onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white"
                        placeholder="••••••••"
                        required
                        minLength={6}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Confirmer le mot de passe</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        data-testid="shop-reset-confirm"
                        type="password"
                        value={authForm.confirmPassword}
                        onChange={(e) => setAuthForm({...authForm, confirmPassword: e.target.value})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white"
                        placeholder="••••••••"
                        required
                        minLength={6}
                      />
                    </div>
                  </div>
                  <button
                    data-testid="shop-reset-submit"
                    type="submit"
                    disabled={authLoading}
                    className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white py-3 rounded-lg font-bold transition-colors"
                  >
                    {authLoading ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
                  </button>
                </form>
              )}

              {/* LOGIN / REGISTER MODE */}
              {(authMode === 'login' || authMode === 'register') && (
              <form onSubmit={authMode === 'login' ? handleLogin : handleRegister} className="space-y-4">
                {authMode === 'register' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Prénom</label>
                        <input
                          type="text"
                          value={authForm.first_name}
                          onChange={(e) => setAuthForm({...authForm, first_name: e.target.value})}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white"
                          required={authMode === 'register'}
                        />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Nom</label>
                        <input
                          type="text"
                          value={authForm.last_name}
                          onChange={(e) => setAuthForm({...authForm, last_name: e.target.value})}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white"
                          required={authMode === 'register'}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Téléphone</label>
                      <input
                        type="tel"
                        value={authForm.phone}
                        onChange={(e) => setAuthForm({...authForm, phone: e.target.value})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Adresse</label>
                      <input
                        type="text"
                        value={authForm.address}
                        onChange={(e) => setAuthForm({...authForm, address: e.target.value})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white"
                        placeholder="Numéro et rue"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Ville</label>
                        <input
                          type="text"
                          value={authForm.city}
                          onChange={(e) => setAuthForm({...authForm, city: e.target.value})}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">Code postal</label>
                        <input
                          type="text"
                          value={authForm.postal_code}
                          onChange={(e) => setAuthForm({...authForm, postal_code: e.target.value})}
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white"
                        />
                      </div>
                    </div>
                  </>
                )}
                
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="email"
                      value={authForm.email}
                      onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white"
                      placeholder="votre@email.com"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Mot de passe</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="password"
                      value={authForm.password}
                      onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white"
                      placeholder="••••••••"
                      required
                      minLength={6}
                    />
                  </div>
                  {authMode === 'login' && (
                    <div className="text-right mt-2">
                      <button
                        data-testid="shop-forgot-password-link"
                        type="button"
                        onClick={() => setAuthMode('forgot')}
                        className="text-red-500 hover:text-red-400 text-xs"
                      >
                        Mot de passe oublié ?
                      </button>
                    </div>
                  )}
                </div>
                
                {authMode === 'register' && (
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Confirmer le mot de passe</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="password"
                        value={authForm.confirmPassword}
                        onChange={(e) => setAuthForm({...authForm, confirmPassword: e.target.value})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white"
                        placeholder="••••••••"
                        required
                        minLength={6}
                      />
                    </div>
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={authLoading}
                  className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white py-3 rounded-lg font-bold transition-colors"
                >
                  {authLoading ? 'Chargement...' : (authMode === 'login' ? 'Se connecter' : 'Créer mon compte')}
                </button>
              </form>
              )}

              {(authMode === 'login' || authMode === 'register') && (
              <div className="mt-6 text-center">
                {authMode === 'login' ? (
                  <p className="text-gray-400">
                    Pas encore de compte ?{' '}
                    <button onClick={() => setAuthMode('register')} className="text-red-500 hover:text-red-400">
                      Créer un compte
                    </button>
                  </p>
                ) : (
                  <p className="text-gray-400">
                    Déjà un compte ?{' '}
                    <button onClick={() => setAuthMode('login')} className="text-red-500 hover:text-red-400">
                      Se connecter
                    </button>
                  </p>
                )}
              </div>
              )}

              {(authMode === 'login' || authMode === 'register') && (
              <div className="mt-4 text-center">
                <p className="text-gray-500 text-sm">
                  Ou <button onClick={() => setShowAuthModal(false)} className="text-white hover:text-red-400">continuer sans compte</button>
                </p>
              </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Account Modal */}
      {showAccount && customer && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a1a1a] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Mon compte</h2>
                <button onClick={() => setShowAccount(false)} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              {/* Tabs */}
              <div className="flex gap-2 mb-6 border-b border-white/10 pb-4">
                <button
                  onClick={() => {}}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
                >
                  <User className="w-4 h-4 inline mr-2" />
                  Profil
                </button>
                <button
                  onClick={() => fetchCustomerOrders()}
                  className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm font-medium"
                >
                  <History className="w-4 h-4 inline mr-2" />
                  Mes commandes ({customerOrders.length})
                </button>
              </div>
              
              {/* Profile Form */}
              <form onSubmit={handleUpdateProfile} className="space-y-4 mb-8">
                <h3 className="text-white font-bold">Informations personnelles</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Prénom</label>
                    <input
                      type="text"
                      value={customer.first_name || ''}
                      onChange={(e) => setCustomer({...customer, first_name: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Nom</label>
                    <input
                      type="text"
                      value={customer.last_name || ''}
                      onChange={(e) => setCustomer({...customer, last_name: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Email</label>
                    <input
                      type="email"
                      value={customer.email || ''}
                      disabled
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Téléphone</label>
                    <input
                      type="tel"
                      value={customer.phone || ''}
                      onChange={(e) => setCustomer({...customer, phone: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white"
                    />
                  </div>
                </div>
                
                <h3 className="text-white font-bold pt-4">Adresse de livraison</h3>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Adresse</label>
                  <input
                    type="text"
                    value={customer.address || ''}
                    onChange={(e) => setCustomer({...customer, address: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Code postal</label>
                    <input
                      type="text"
                      value={customer.postal_code || ''}
                      onChange={(e) => setCustomer({...customer, postal_code: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Ville</label>
                    <input
                      type="text"
                      value={customer.city || ''}
                      onChange={(e) => setCustomer({...customer, city: e.target.value})}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white"
                    />
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={authLoading}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white px-6 py-2.5 rounded-lg font-medium"
                >
                  {authLoading ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </button>
              </form>
              
              {/* Orders List */}
              {customerOrders.length > 0 && (
                <div>
                  <h3 className="text-white font-bold mb-4">Mes commandes récentes</h3>
                  <div className="space-y-3">
                    {customerOrders.slice(0, 5).map((order) => (
                      <div key={order.id} className="bg-white/5 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white font-medium">
                            Commande #{order.id.slice(0, 8)}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            order.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                            order.status === 'shipped' ? 'bg-purple-500/20 text-purple-400' :
                            order.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {order.status === 'delivered' ? 'Livrée' :
                             order.status === 'shipped' ? 'Expédiée' :
                             order.status === 'cancelled' ? 'Annulée' :
                             order.status === 'confirmed' ? 'Confirmée' : 'En attente'}
                          </span>
                        </div>
                        <div className="text-gray-400 text-sm">
                          <p>{order.items?.length || 0} article(s) • {order.total?.toFixed(2) || '0.00'} € TTC</p>
                          <p>Commandé le {new Date(order.created_at).toLocaleDateString('fr-FR')}</p>
                          {order.tracking_number && (
                            <p className="text-purple-400 mt-1">N° suivi : {order.tracking_number}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Change password */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Lock className="w-4 h-4" /> Changer mon mot de passe
                </h3>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">Mot de passe actuel</label>
                    <input
                      data-testid="shop-account-current-pwd"
                      type="password"
                      value={changePwdForm.current_password}
                      onChange={(e) => setChangePwdForm({ ...changePwdForm, current_password: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Nouveau mot de passe</label>
                      <input
                        data-testid="shop-account-new-pwd"
                        type="password"
                        value={changePwdForm.new_password}
                        onChange={(e) => setChangePwdForm({ ...changePwdForm, new_password: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white"
                        required
                        minLength={6}
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1">Confirmer</label>
                      <input
                        data-testid="shop-account-confirm-pwd"
                        type="password"
                        value={changePwdForm.confirm_new_password}
                        onChange={(e) => setChangePwdForm({ ...changePwdForm, confirm_new_password: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white"
                        required
                        minLength={6}
                      />
                    </div>
                  </div>
                  <button
                    data-testid="shop-account-change-pwd-submit"
                    type="submit"
                    disabled={changePwdLoading}
                    className="bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white px-6 py-2.5 rounded-lg font-medium"
                  >
                    {changePwdLoading ? 'Modification...' : 'Modifier le mot de passe'}
                  </button>
                </form>
              </div>

              {/* Logout */}
              <div className="mt-8 pt-4 border-t border-white/10">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-gray-400 hover:text-red-400 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  Se déconnecter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop;
